import React, { useState } from "react";
import { ButtonComp, CardComp } from "../../../../components";
import {
  useGetLoanRequestDetailsQuery,
  useRemoveGuarantorMutation,
  useAddGuarantorToLoanMutation,
} from "../../../../store/admin/loan";
import { useGetAllMembersQuery } from "../../../../store/selectableSlice";
import { SelectCompWithFormattedLabel } from "../../../../components/Ui/selectComp";
import AddNonMemberGuarantorForm from "./AddNonMemberGuarantorForm";
import { AiOutlineClose } from "react-icons/ai";
import { toast } from "react-hot-toast";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import { convertToTitleCase } from "../../../../utils/helpers";

const ManageGuarantorsModal = ({ requestId, setModalPage }) => {
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showAddNonMemberModal, setShowAddNonMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [nonMemberGuarantorData, setNonMemberGuarantorData] = useState(null);

  const {
    data: loanDetails,
    isLoading,
    refetch,
  } = useGetLoanRequestDetailsQuery(requestId, {
    skip: !requestId,
  });

  const { data: membersData = [] } = useGetAllMembersQuery();
  const [removeGuarantor, { isLoading: isRemoving }] = useRemoveGuarantorMutation();
  const [addGuarantor, { isLoading: isAdding }] = useAddGuarantorToLoanMutation();

  const loan = loanDetails || {};
  const guarantors = loan?.guarantors || [];

  // Get list of member IDs that are already guarantors
  const existingGuarantorMemberIds = React.useMemo(() => {
    return guarantors
      .filter((g) => g.member_id)
      .map((g) => g.member_id);
  }, [guarantors]);

  const members = Array.isArray(membersData) ? membersData : [];
  const formattedMembers = React.useMemo(() => {
    return members
      .filter((item) => item && item.id && !existingGuarantorMemberIds.includes(item.id))
      .map((item) => {
        const first = item.first_name || "";
        const last = item.last_name || "";
        const name =
          `${first} ${last}`.trim() || item.name || "Unknown";
        const canGuarantee = item.can_guarantee !== false;
        const reason = item.guarantee_block_reason || null;
        return {
          label: reason ? `${name} (${reason})` : name,
          name,
          photo: item.photo,
          userId: item.member_no,
          value: item.id,
          isDisabled: !canGuarantee,
          can_guarantee: canGuarantee,
          guarantee_block_reason: reason,
        };
      });
  }, [members, existingGuarantorMemberIds]);

  const handleRemoveGuarantor = async (guarantorId) => {
    if (!window.confirm("Are you sure you want to remove this guarantor?")) {
      return;
    }

    try {
      const result = await removeGuarantor({
        requestId,
        guarantorId,
      }).unwrap();

      if (result.status) {
        toast.success(result.message || "Guarantor removed successfully");
        refetch();
      } else {
        toast.error(result.message || "Failed to remove guarantor");
      }
    } catch (error) {
      toast.error(error?.data?.message || "Failed to remove guarantor");
    }
  };

  const handleAddMemberGuarantor = async () => {
    if (!selectedMember?.value) {
      toast.error("Please select a member");
      return;
    }
    if (selectedMember?.isDisabled || selectedMember?.can_guarantee === false) {
      toast.error(
        selectedMember?.guarantee_block_reason ||
          "This member cannot guarantee this loan"
      );
      return;
    }

    try {
      const result = await addGuarantor({
        requestId,
        payload: {
          member_id: selectedMember.value,
        },
      }).unwrap();

      if (result.status) {
        toast.success(result.message || "Guarantor added successfully");
        setSelectedMember(null);
        setShowAddMemberModal(false);
        refetch();
      } else {
        toast.error(result.message || "Failed to add guarantor");
      }
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add guarantor");
    }
  };

  const handleAddNonMemberGuarantor = async (data) => {
    if (!data) {
      toast.error("Please fill in the non-member guarantor details");
      return false; // Return false to indicate failure
    }

    try {
      // Ensure phone is a string
      const payload = {
        ...data,
        phone: String(data.phone || ""),
      };

      const result = await addGuarantor({
        requestId,
        payload: payload,
      }).unwrap();

      if (result.status) {
        toast.success(result.message || "Guarantor added successfully");
        setNonMemberGuarantorData(null);
        setShowAddNonMemberModal(false);
        refetch();
        return true; // Return true to indicate success
      } else {
        toast.error(result.message || "Failed to add guarantor");
        return false; // Return false to indicate failure
      }
    } catch (error) {
      const errorMessage = error?.data?.message || error?.message || "Failed to add guarantor";
      toast.error(errorMessage);
      return false; // Return false to indicate failure
    }
  };

  // Wrapper to capture the form submission
  const handleNonMemberFormSubmit = (prev) => {
    // The form calls: setNonMemberGuarantorDetails((initial) => [...initial, values])
    // We intercept this to extract the new value (values) and call our API
    // Since we can't directly access 'values', we'll use a ref or state to track it
    return prev;
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case "CONFIRMED":
        return "#19bc31";
      case "DECLINED":
        return "#ff4343";
      case "PENDING":
      default:
        return "#ff9500";
    }
  };

  if (isLoading) {
    return (
      <div className="relative p-4">
        <AiOutlineClose
          onClick={() => setModalPage()}
          className="absolute top-4 right-4 cursor-pointer"
          size={24}
          style={{ color: "var(--color-primary)" }}
        />
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="p-4" style={{ maxHeight: "80vh", overflowY: "auto" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold mb-0 text-black1">Manage Guarantors</h4>
        <AiOutlineClose
          onClick={() => setModalPage()}
          className="cursor-pointer"
          size={24}
          style={{ color: "var(--color-primary)" }}
        />
      </div>

      {/* Loan Info */}
      <CardComp cardClassName="mb-4 p-3" style={{ backgroundColor: "#f7f7f7" }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <small className="text-muted d-block">Loan Applicant</small>
            <h6 className="mb-0 fw-bold">
              {loan?.member?.first_name} {loan?.member?.last_name}
            </h6>
          </div>
          <div className="text-end">
            <small className="text-muted d-block">Loan Amount</small>
            <h6 className="mb-0 fw-bold text-primary1">
              {loan?.amount?.toLocaleString("en-NG", {
                style: "currency",
                currency: "NGN",
              })}
            </h6>
          </div>
        </div>
        {loan?.loan_type && (
          <div className="mt-2 pt-2 border-top">
            <small className="text-muted">
              Required Guarantors: {loan.loan_type.min_guarantor} - {loan.loan_type.max_guarantor}
            </small>
            <br />
            <small className="text-muted">
              Current: {guarantors.length} guarantor(s)
            </small>
          </div>
        )}
      </CardComp>

      {/* Current Guarantors */}
      <div className="mb-4">
        <h5 className="fw-bold mb-3 text-black1">Current Guarantors</h5>
        {guarantors.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {guarantors.map((guarantor) => (
              <CardComp
                key={guarantor.id}
                cardClassName="p-3"
                style={{
                  border: "1px solid #e5e5e5",
                  backgroundColor: "#ffffff",
                }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="d-flex align-items-start flex-grow-1">
                    <FallbackImage
                      src={guarantor?.photo || guarantor?.member?.photo}
                      width="48px"
                      height="48px"
                      className="rounded-circle me-3"
                      style={{ objectFit: "cover" }}
                    />
                    <div className="flex-grow-1">
                      <h6 className="mb-1 fw-bold">{guarantor.name}</h6>
                      <div
                        className="px-2 py-1 rounded-pill text-white d-inline-block mb-2"
                        style={{
                          backgroundColor: getStatusBadgeColor(guarantor.status),
                          fontSize: "0.75rem",
                        }}
                      >
                        {convertToTitleCase(guarantor.status || "PENDING")}
                      </div>
                      <div className="small text-muted">
                        {guarantor.email && (
                          <div className="mb-1">Email: {guarantor.email}</div>
                        )}
                        {guarantor.phone && (
                          <div className="mb-1">Phone: {guarantor.phone}</div>
                        )}
                        {guarantor.member_id && (
                          <div>Membership Number: {guarantor?.member?.member_no || "N/A"}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  {guarantor.status !== "CONFIRMED" && (
                    <button
                      type="button"
                      className="btn btn-sm text-danger border-0 bg-transparent"
                      onClick={() => handleRemoveGuarantor(guarantor.id)}
                      disabled={isRemoving}
                      style={{ fontSize: "24px", fontWeight: "bold" }}
                      title="Remove guarantor"
                    >
                      ×
                    </button>
                  )}
                </div>
              </CardComp>
            ))}
          </div>
        ) : (
          <p className="text-muted">No guarantors assigned</p>
        )}
      </div>

      {/* Add Guarantor Section */}
      {loan?.loan_type && guarantors.length < (loan.loan_type.max_guarantor || 999) && (
        <div className="mb-4">
          <h5 className="fw-bold mb-3 text-black1">Add New Guarantor</h5>
          <div className="d-flex flex-column flex-md-row gap-3">
            <ButtonComp
              btnText="Add Member Guarantor"
              btnClassName="flex-fill text-white border-0 py-3 fw-semibold"
              onClick={() => setShowAddMemberModal(true)}
              style={{
                borderRadius: "10px",
                backgroundColor: "var(--color-primary)",
                minHeight: "48px",
                transition: "all 0.3s ease",
                boxShadow: "0 2px 8px var(--color-border)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "color-mix(in srgb, var(--color-primary) 80%, black)";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(139, 49, 58, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "var(--color-primary)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px var(--color-border)";
              }}
            />
            <ButtonComp
              btnText="Add Non-Member Guarantor"
              btnClassName="flex-fill text-primary1 border-2 py-3 fw-semibold"
              onClick={() => setShowAddNonMemberModal(true)}
              style={{
                borderRadius: "10px",
                backgroundColor: "#ffffff",
                borderColor: "var(--color-primary)",
                borderWidth: "2px",
                minHeight: "48px",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f7f7f7";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(139, 49, 58, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>
        </div>
      )}

      {/* Add Member Guarantor Modal */}
      {showAddMemberModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1060,
          }}
          onMouseDown={(e) => {
            // Only close if clicking directly on the overlay, not on child elements
            if (e.target === e.currentTarget) {
              setShowAddMemberModal(false);
            }
          }}
        >
          <CardComp
            cardClassName="p-4"
            style={{ maxWidth: "500px", width: "90%" }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">Add Member Guarantor</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowAddMemberModal(false)}
                style={{ fontSize: "18px" }}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold mb-2">Select Member</label>
              {formattedMembers.length === 0 ? (
                <div className="alert alert-info mb-0" style={{ fontSize: "14px" }}>
                  <i className="fas fa-info-circle me-2"></i>
                  All available members are already guarantors for this loan.
                </div>
              ) : (
                <div onMouseDown={(e) => e.stopPropagation()}>
                  <SelectCompWithFormattedLabel
                    arrayComp={formattedMembers}
                    selectedOption={selectedMember}
                    setOption={setSelectedMember}
                    placeholder="Search and select a member..."
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 1070 }),
                    }}
                  />
                </div>
              )}
            </div>
            <div className="d-flex gap-3 mt-4">
              <ButtonComp
                btnText="Cancel"
                btnClassName="flex-fill text-black1 border-0 py-3 fw-semibold"
                onClick={() => setShowAddMemberModal(false)}
                style={{
                  borderRadius: "10px",
                  backgroundColor: "#f7f7f7",
                  borderColor: "#e5e5e5",
                  minHeight: "48px",
                  transition: "all 0.3s ease",
                  color: "#45464e",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e5e5";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f7f7f7";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
              <ButtonComp
                btnText={isAdding ? "Adding..." : "Add Guarantor"}
                btnClassName="flex-fill text-white border-0 py-3 fw-semibold"
                onClick={handleAddMemberGuarantor}
                disabled={isAdding || !selectedMember}
                loading={isAdding}
                style={{
                  borderRadius: "10px",
                  backgroundColor: isAdding || !selectedMember ? "#a1a0a0" : "var(--color-primary)",
                  minHeight: "48px",
                  transition: "all 0.3s ease",
                  boxShadow: isAdding || !selectedMember ? "none" : "0 2px 8px var(--color-border)",
                }}
                onMouseEnter={(e) => {
                  if (!isAdding && selectedMember) {
                    e.currentTarget.style.backgroundColor = "color-mix(in srgb, var(--color-primary) 80%, black)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(139, 49, 58, 0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isAdding && selectedMember) {
                    e.currentTarget.style.backgroundColor = "var(--color-primary)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px var(--color-border)";
                  }
                }}
              />
            </div>
          </CardComp>
        </div>
      )}

      {/* Non-Member Guarantor Modal */}
      {showAddNonMemberModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1060,
          }}
          onMouseDown={(e) => {
            // Only close if clicking directly on the overlay, not on child elements
            if (e.target === e.currentTarget) {
              setShowAddNonMemberModal(false);
            }
          }}
        >
          <CardComp
            cardClassName="p-0"
            style={{ maxWidth: "700px", width: "90%", maxHeight: "90vh", overflowY: "auto" }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <AddNonMemberGuarantorForm
              Back={() => setShowAddNonMemberModal(false)}
              onSubmit={handleAddNonMemberGuarantor}
              isLoading={isAdding}
            />
          </CardComp>
        </div>
      )}
    </div>
  );
};

export default ManageGuarantorsModal;

